import React, { useContext, useEffect, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/styles';
import IconButton from '@material-ui/core/IconButton';
import withResponsive from 'components/Layout/withResponsive';
import { Link, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { toggleDestroyModal } from 'redux/actions/destroyModalActions';
import {
  destroyMessage,
  downloadFile,
  getMessageBulkFiles,
  getMessage,
  patchMessage,
  setAutoCategorizedDateMessage,
  resetMessage,
} from 'redux/actions/messageActions';
import LoadingIcon from 'mdi-react/LoadingIcon';

import ReplyStatusBadge from 'components/Inbox/InboxList/ReplyStatusBadge';
import OpportunityStageBadge from 'components/Inbox/InboxList/OpportunityStageBadge';
import { isEmpty, isNil, get, debounce } from 'lodash';
import CloseIcon from 'mdi-react/CloseIcon';
import TrashIcon from 'icons/trash';
import { ReactComponent as AccountIcon } from 'icons/account.svg';
import { ReactComponent as SequenceIcon } from 'icons/sequence.svg';
import { ReactComponent as RemoveIcon } from 'icons/remove.svg';
import { ReactComponent as OpportunityIcon } from 'icons/inboxOpportunity.svg';
import { Box, Divider, Grid } from '@material-ui/core';
import ReplyAllIcon from '@material-ui/icons/ReplyAll';
import ReplyIcon from '@material-ui/icons/Reply';
import ForwardIcon from '@material-ui/icons/Forward';
import { MIN_DESKTOP_WIDTH, MAX_MEDIUM_SIZE } from 'constants/misc';
import DestroyModal from 'components/modals/DestroyModal';
import Button from 'components/buttons/Button';
import { sequenceTabUrl } from 'constants/routing';
import { Can, AbilityContext } from 'utils/permissions';
import { useDocumentTitle } from 'utils/hooks';
import { REPLY_STATUS_OPTIONS } from 'constants/outreach';
import { updateOutreachThreadStatus } from 'redux/actions/outreachActions';
import { secondary900 } from 'themes/colors';
import classNames from 'classnames';
import { getOpportunities, patchOpportunity } from 'redux/actions/opportunityActions';
import MessageEditor from './MessageEditor';
import Message from './Message';
import SearchDropDownTab from '../../Tabs/SearchDropDownTab';
import AddOpportunity from '../../Opportunities/AddOpportunity';
import OpportunityView from '../../Opportunities/OpportunityView';


const styles = theme => ({
  root: {
    width: '0%',
    height: '100vh',
    maxWidth: '100%',
    backgroundColor: theme.palette.white,
    position: 'relative',
    boxShadow: theme.shadows[0],
    overflow: 'hidden',
    transition: 'width 1s',
    transform: 'translateX(100%)',
    '&$isOpen': {
      transform: 'translateX(0)',
      width: '50%',
    },
    [`@media screen and (max-width: ${MAX_MEDIUM_SIZE}px)`]: {
      position: 'absolute',
      zIndex: '1',
      top: '0',
      right: '0',
      bottom: '0',
      [`@media screen and (min-width: ${MIN_DESKTOP_WIDTH}px)`]: {
        '&$isOpen': {
          width: 'calc(100% - 15.125rem) !important',
        },
      },
      '&$sidebarCollapsed': {
        [`@media screen and (min-width: ${MIN_DESKTOP_WIDTH}px)`]: {
          '&$isOpen': {
            width: 'calc(100% - 4.125rem) !important',
          },
        },
        [`@media screen and (max-width: ${MIN_DESKTOP_WIDTH}px)`]: {
          '&$isOpen': {
            width: '100% !important',
          },
          height: 'calc(100% - 3.5rem)',
          bottom: '0',
          top: 'unset',
        },
      },
    },
  },
  sidebarCollapsed: {},
  isOpen: {},
  wrapper: {
    height: '100%',
    padding: '2.625rem 1.5rem',
  },
  closeIcon: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    color: theme.palette.secondary['300'],
    cursor: 'pointer',

  },
  subject: {
    color: theme.palette.secondary.text,
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  badgesContainer: {
    marginTop: '1.5rem',
    marginBottom: '1.5rem',
    display: 'grid',
    gridGap: '0.5rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(0.625rem, max-content))',
  },
  messagesContainer: {
    overflowY: 'auto',
    height: '100%',
    maxHeight: 'calc(100vh - 17.438rem)',
  },
  messageDetail: {
    fontSize: '0.75rem',
    color: theme.palette.secondary['900'],
  },
  messageDetailTitle: {
    fontWeight: 400,
    '& svg': {
      marginRight: '0.625rem',
    },
  },
  messageDetailContent: {
    fontWeight: 600,
  },
  messageDetailContentDivider: {
    margin: '0.675rem 0',
  },
  btnIcon: {
    color: theme.palette.secondary['300'],
    marginRight: '0.563rem',
    cursor: 'pointer',
    '&:hover': {
      fill: theme.palette.primary.main,
    },
  },
  footerBtn: {
    height: '2.5rem',
    fontFamily: theme.typography.fontFamily,
    padding: '0.063rem 0.813rem 0.063rem 0.563rem',
    border: `1px solid ${theme.palette.secondary['300']}`,
    borderRadius: '0.375rem',
    color: theme.palette.secondary['900'],
    backgroundColor: theme.palette.white,
    fontSize: '0.875rem',
    fontWeight: 600,
    letterSpacing: '-0.013rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    cursor: 'pointer',
    marginLeft: '1rem',
    '&:hover, &$opened': {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.primary['100'],
      border: `1px solid ${theme.palette.primary.main}`,
      '& $btnIcon': {
        fill: theme.palette.primary.main,
      },
    },
  },
  footer: {
    bottom: '0',
    width: '100%',
    borderTop: `1px solid ${theme.palette.secondary['100']}`,
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '1.25rem 1.5rem',
    backgroundColor: theme.palette.white,
  },
  replyContainer: {
    transition: 'all ease-in-out 0.3s !important',
    height: 'auto',
    display: 'none',
    bottom: '0',
    width: '100%',
    padding: '1.5rem 0',
    boxShadow: theme.shadows[0],
    backgroundColor: theme.palette.white,
    maxHeight: '100%',
    overflow: 'auto',
    '&$openedReply': {
      display: 'block',
    },
  },
  openedReply: {},
  trashIcon: {
    cursor: 'pointer',
    color: theme.palette.secondary['300'],
    '&:hover': {
      color: theme.palette.accent3['800'],
    },
  },
  messageTrashIcon: {
    position: 'absolute',
    top: '3.5rem',
    right: '0.8rem',
  },
  replyTrashIcon: {
    float: 'right',
    marginTop: '1.5rem',
  },
  opportunityBtn: {
    height: '1.5rem',
    border: '1px dashed',
    borderRadius: '6px',
  },
  crossImg: {
    padding: 4,
  },
  customLink: {
    color: secondary900,
    textDecoration: 'none',
    '&:focus, &:hover, &:visited, &:link, &:active': {
      color: secondary900,
      textDecoration: 'none',
    },
  },
  loaderPosition: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100vw',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100vh',
  },
});

const InboxDetail = (props) => {
  const {
    classes, message, sidebar, loading,
  } = props;
  const ability = useContext(AbilityContext);
  const lastMessage = message.last_message || message;
  const [messageEditor, setMessageEditor] = React.useState({ opened: false, lastMessage });
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [opportunityLoad, setOpportunityLoad] = React.useState(false);
  const [openOpportunityView, setOpenOpportunityView] = React.useState(false);
  const [openOpportunityModal, setOpenOpportunityModal] = React.useState(false);
  const [opportunityTitle, setOpportunityTitle] = React.useState(null);
  const ref = useRef();
  const [isLoadingLocalComponent, setIsLoadingLocalComponent] = React.useState(true);
  const toggleOpportunityModal = (title) => {
    setOpportunityTitle(title);
    setOpenOpportunityModal(prevState => !prevState);
    setAnchorEl(null);
  };
  const toggleOpportunityView = () => {
    setOpenOpportunityView(prevState => !prevState);
  };
  useDocumentTitle(message?.subject);
  React.useEffect(() => {
    const effect = async () => {
      if (message.id) {
        if (!message.loading && !!message.loaded) {
          const messageIds = [];
          if (isNil(message.files)) {
            messageIds.push(message.id);
          }
          if (!isEmpty(message.messages)) {
            message.messages.forEach((msg) => {
              if (isNil(msg.files)) {
                messageIds.push(msg.id);
              }
            });
          }
          if (!isEmpty(messageIds)) {
            await props.getMessageBulkFiles(messageIds);
            setIsLoadingLocalComponent(false);
          } else {
            setIsLoadingLocalComponent(false);
          }
        }
        setMessageEditor({ opened: false, lastMessage });
      }
    };
    effect();
  }, [message.id, message.messages]);

  const onSuccessOutreachDestroyed = () => {
    props.toggleDestroyModal();
    props.toggleInboxDetail();
  };

  const closeMessageDetail = () => {
    props.toggleInboxDetail();
    props.resetMessage();
  };

  const handleChangeStatus = async (status) => {
    const triggerStatus = ['not_now', 'interested', 'sponsorship'];
    const newStatus = REPLY_STATUS_OPTIONS.find(x => x.value === status.value);
    setOpportunityLoad(true);
    await props.updateOutreachThreadStatus(
      message.outreach,
      {
        thread_status: {
          code: newStatus.value,
          verbose: newStatus.label,
        },
      },
      true, // seamless
    );
    if (triggerStatus.includes(newStatus.value)) {
      await props.getMessage(message.id);
    }
    setOpportunityLoad(false);
    props.setAutoCategorizedDateMessage({
      messageId: message.id,
      autoCategorizedDate: null,
    });
  };

  const canChangeStatus = !isEmpty(message.outreach) && ability.can('changeStatus', 'Message');

  const updateOpportunityHandle = async (stageValue) => {
    const opp = message.opportunity;
    await props.patchOpportunity(opp, { stage: stageValue });
  };
  const updateMessage = async (event, id) => {
    setAnchorEl(null);
    event.preventDefault();
    await props.patchMessage(
      message.id,
      { opportunity_id: id },
    );
  };
  const handleGetOpportunities = async (search) => {
    const accountIDs = get(message, 'email_service_provider_config_accounts', 0);
    await props.getOpportunities({
      search,
      account_id: accountIDs.length > 0 ? accountIDs.map(l => (l)).join(',') : 0,
    });
  };

  useEffect(() => {
    if (ref.current && messageEditor.opened) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    }
  }, [messageEditor.opened]);

  const debouncedHandleGetOpportunities = debounce(handleGetOpportunities, 300);
  const isLoadingInterface = (message.loading || loading || isLoadingLocalComponent);
  return (
    <>
      <div className={
        classNames(
          classes.root, { [classes.sidebarCollapsed]: sidebar.collapse },
          { [classes.isOpen]: message.isOpen },
          )
      }
      >
        {isLoadingInterface && <div className="loader"><LoadingIcon /></div>}
        {message.isOpen &&
          <DestroyModal
            funcToExecute={props.destroyMessage}
            onSuccess={onSuccessOutreachDestroyed}
          />
        }
        {message.loaded && (
              <>
                <CloseIcon className={classes.closeIcon} onClick={(closeMessageDetail)} />
                <Can I="delete" a="Message">
                  <TrashIcon
                    className={`${classes.trashIcon} ${classes.messageTrashIcon}`}
                    onClick={() => {
                      props.toggleDestroyModal(message.id, 'this message');
                    }}
                  />
                </Can>
              </>
            )}
        <div className={classes.wrapper}>
          <div className={`${classes.subject} mb-2`}>{message.subject}</div>
          {message.account &&
            <Grid container className={classes.messageDetail}>
              <Grid item sm={4} className={classes.messageDetailTitle}>
                <AccountIcon /> Account
              </Grid>
              <Grid item className={classes.messageDetailContent}>
                {message.loading ? <div className="loading-dots" /> : message.account.name}
              </Grid>
            </Grid>
          }
          {message.sequence && <><Divider className={classes.messageDetailContentDivider} />
            <Grid container className={classes.messageDetail}>
              <Grid item sm={4} className={classes.messageDetailTitle}>
                <SequenceIcon /> Sequence
              </Grid>
              <Grid item className={classes.messageDetailContent}>
                {message.loading ? <div className="loading-dots" /> :
                <Link
                  className={classes.customLink}
                  to={sequenceTabUrl(message.sequence.id)}
                  target="_blank"
                >
                  {message.sequence.name}
                </Link>}
              </Grid>
            </Grid></>}
          { !message.opportunity && opportunityLoad && <div className="loading-dots" />}
          {message.opportunity && <><Divider className={classes.messageDetailContentDivider} />
            <Grid container className={classes.messageDetail}>
              <Grid item sm={4} className={classes.messageDetailTitle}>
                <OpportunityIcon /> Opportunity
              </Grid>
              <Grid item sm={5} className={classes.messageDetailContent} onClick={toggleOpportunityView}>
                {message.loading ? <div className="loading-dots" /> : <div style={{ cursor: 'pointer' }}>{message.opportunity.title}</div>}
              </Grid>
              <Grid item sm={3}>
                <Box display="flex" justifyContent="flex-end">
                  <OpportunityStageBadge
                    status={message.opportunity.stage}
                    handleStatusChange={ability.can('edit', 'Opportunity') ? updateOpportunityHandle :
                    null}
                  />
                  <Can I="edit" a="Opportunity">
                    <IconButton className={classes.crossImg} onClick={e => updateMessage(e, null)}>
                      <RemoveIcon />
                    </IconButton>
                  </Can>
                </Box>
              </Grid>
            </Grid></>}
          <div className={classes.badgesContainer}>
            {message.loaded && message.outreach && (
              <ReplyStatusBadge
                status={message.reply_status}
                handleChangeStatus={canChangeStatus ? handleChangeStatus : null}
                autoCategorized={!isEmpty(message.auto_categorized_date)}
                labelPadding
              />
            )}
            {!message.opportunity && <>
              <Can I="add" a="Opportunity">
                <Button
                  className={classes.opportunityBtn}
                  variant="outlined"
                  onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                    handleGetOpportunities();
                  }}
                  size="medium"
                >
                  + Link an Opportunity
                </Button>
              </Can>
              <SearchDropDownTab
                options={props.opportunities.results}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                toggle={toggleOpportunityModal}
                seachPlaceholder="Find an opportunity"
                handleSearch={debouncedHandleGetOpportunities}
                isLoading={props.opportunities.loading}
                actionHandler={updateMessage}
                position={{
                  anchor: { vertical: 'bottom', horizontal: 'left' },
                  transform: { vertical: 'top', horizontal: 'left' },
                }}
              />
            </>}
          </div>
          {message.loaded && (
            <div className={classes.messagesContainer}>
              {get(message, ['messages'], []).map((m, i) => (
                <Message
                  key={m.id}
                  message={m}
                  isOpen={i === 0}
                  setMessageEditor={setMessageEditor}
                  downloadFile={props.downloadFile}
                  parentMessage={message}
                />
              ))}
              {message &&
                <Message
                  key={message.id}
                  message={message}
                  isOpen={!message.messages_amount}
                  setMessageEditor={setMessageEditor}
                  downloadFile={props.downloadFile}
                />
              }
              <div
                ref={ref}
                className={classNames(classes.replyContainer, { [classes.openedReply]: messageEditor.opened })}
              >
                <TrashIcon
                  className={`${classes.trashIcon} ${classes.replyTrashIcon}`}
                  onClick={() => setMessageEditor({ opened: false, message: null })}
                />
                <MessageEditor
                  type={messageEditor.opened}
                  message={messageEditor.message}
                  setMessageEditor={setMessageEditor}
                  isDisabled={message.reply_status === 'bounced'}
                  toggleInboxDetail={props.toggleInboxDetail}
                />
              </div>
              {message.loaded && !messageEditor.opened && (
                <div className={classes.footer}>
                  <button
                    className={classes.footerBtn}
                    onClick={() => setMessageEditor({ opened: 'REPLY', message: lastMessage })}
                  >
                    <ReplyIcon className={classes.btnIcon} viewBox="0 0 24 24" />
                      Reply
                  </button>
                  <button
                    className={classes.footerBtn}
                    onClick={() => setMessageEditor({ opened: 'REPLY_ALL', message: lastMessage })}
                  >
                    <ReplyAllIcon className={classes.btnIcon} viewBox="0 0 24 24" />
                      Reply all
                  </button>
                  <button
                    className={classes.footerBtn}
                    onClick={() => setMessageEditor({ opened: 'FORWARD', message: lastMessage })}
                  >
                    <ForwardIcon className={classes.btnIcon} viewBox="0 0 24 24" />
                      Forward
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        {openOpportunityModal && (
          <AddOpportunity
            toggle={toggleOpportunityModal}
            initialValues={opportunityTitle && { title: opportunityTitle }}
          />
        )}
        {openOpportunityView && (
          <OpportunityView
            toggle={toggleOpportunityView}
            opportunityId={message.opportunity.id}
          />
        )}
      </div>
      {opportunityLoad && (
        <div
          className={`loader ${classes.loaderPosition}`}
        >
          <LoadingIcon />
        </div>
      )}
  </>
  );
};

InboxDetail.propTypes = {
  classes: PropTypes.element.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  message: PropTypes.shape({
    id: PropTypes.string.isRequired,
    messages: PropTypes.shape({
      map: PropTypes.func.isRequired,
    }).isRequired,
    messages_amount: PropTypes.number.isRequired,
  }).isRequired,
  toggleInboxDetail: PropTypes.func.isRequired,
  resetMessage: PropTypes.func.isRequired,
  sidebar: PropTypes.shape({}).isRequired,
  toggleDestroyModal: PropTypes.func.isRequired,
  destroyMessage: PropTypes.func.isRequired,
  updateOutreachThreadStatus: PropTypes.func.isRequired,
  setAutoCategorizedDateMessage: PropTypes.func.isRequired,
  getMessageBulkFiles: PropTypes.func.isRequired,
  downloadFile: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  opportunities: PropTypes.shape({
    loading: PropTypes.bool,
    loaded: PropTypes.bool,
    results: PropTypes.array,
  }).isRequired,
  getOpportunities: PropTypes.func.isRequired,
  patchOpportunity: PropTypes.func.isRequired,
  getMessage: PropTypes.func.isRequired,
  patchMessage: PropTypes.func.isRequired,
};

InboxDetail.defaultProps = {
  loading: false,
};

export default compose(
  withRouter,
  withResponsive,
  withStyles(styles, { withTheme: true }),
  connect(
    state => ({
      opportunities: state.opportunities,
      sidebar: state.sidebar,
    }),
    {
      toggleDestroyModal,
      destroyMessage,
      updateOutreachThreadStatus,
      setAutoCategorizedDateMessage,
      downloadFile,
      getMessageBulkFiles,
      getMessage,
      patchMessage,
      getOpportunities,
      patchOpportunity,
      resetMessage,
    },
  ),
)(InboxDetail);
